import { useEffect, useRef } from 'react';
import { useUploadProgress } from './useUploadProgress';
import { Folder } from './useFolders';

/**
 * Checks if `targetFolderId` equals `currentFolderId`
 * or is an ancestor of it (walking up parent_folder_id).
 */
function isCurrentOrAncestor(
  targetFolderId: number,
  currentFolderId: number,
  folders: Folder[]
): boolean {
  let folderId: number | undefined = currentFolderId;
  while (folderId !== undefined) {
    if (folderId === targetFolderId) return true;
    const folder = folders.find(f => f.folder_id === folderId);
    folderId = folder?.parent_folder_id;
  }
  return false;
}

/**
 * Auto-refreshes folder data when an upload batch completes,
 * but only if the batch's target folder is the current folder or an ancestor of it.
 */
export function useBatchCompletionRefresh(
  matterGuid: string | undefined,
  currentFolderId: number | undefined,
  folders: Folder[],
  onRefresh: () => void
) {
  const { activeBatches } = useUploadProgress(matterGuid);
  const prevStatusesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const prevStatuses = prevStatusesRef.current;
    let shouldRefresh = false;

    for (const batch of activeBatches) {
      const prevStatus = prevStatuses[batch.batchTaskId];
      // Detect transition TO completed (skip if we never saw it before)
      if (
        batch.status === 'completed' &&
        prevStatus !== undefined &&
        prevStatus !== 'completed'
      ) {
        if (
          currentFolderId &&
          batch.folderId &&
          isCurrentOrAncestor(batch.folderId, currentFolderId, folders)
        ) {
          shouldRefresh = true;
        }
      }
    }

    // Update tracked statuses
    const newStatuses: Record<string, string> = {};
    for (const batch of activeBatches) {
      newStatuses[batch.batchTaskId] = batch.status;
    }
    prevStatusesRef.current = newStatuses;

    if (shouldRefresh) {
      onRefresh();
    }
  }, [activeBatches, currentFolderId, folders, onRefresh]);
}
